import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import api from "../lib/api";
import {
  Sparkle, Calculator, ArrowLeft, MoonStars, Compass, House,
  Star, Target, ArrowsInLineHorizontal, CalendarBlank, ArrowDown, ArrowUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import { useSavedReportLoader } from "../lib/useSavedReport";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";

export default function BhriguBinduPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useSavedReportLoader({ expectedTool: "bhrigu", setForm, setResult });

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const analyze = async (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bhrigu/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='bhrigu-result']")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 100);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your Bhrigu Bindu. Please re-check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="bhrigu-page">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button onClick={() => navigate(-1)} className="md:hidden flex items-center gap-1 text-sm text-muted mb-4" data-testid="bhrigu-back-btn">
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Target size={14} weight="duotone" /> Free · Bhrigu Nadi · Maharishi Bhrigu's Karmic Point
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Bhrigu <span className="italic text-gold">Bindu</span> Calculator
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-2xl font-light">
          The Bhrigu Bindu is the midpoint of the arc going zodiacally <b className="text-[#3F2D1B]">forward from Rahu to Moon</b> — the single most karmically-loaded degree in your chart. Rahu marks where your soul's hunger from past lives sits; Moon marks your mind in this life. The midpoint of the journey Rahu → Moon reveals the *purpose* of this incarnation. When Jupiter, Saturn, Rahu or Ketu transit this exact degree, life-shifting karmic chapters unfold — marriages, deaths, awakenings, abundance, voyages.
        </p>

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input type="date" required value={form.date} onChange={handle("date")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="bhrigu-date" />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input type="time" required value={form.time} onChange={handle("time")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="bhrigu-time" />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) =>
                    setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
                  }
                  placeholder="Mumbai, India"
                  testId="bhrigu-place"
                />
              </div>
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Timezone</span>
              <select value={form.timezone} onChange={handle("timezone")} className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg" data-testid="bhrigu-tz">
                <option value="Asia/Kolkata">India (IST · UTC+5:30)</option>
                <option value="Asia/Dubai">UAE (UTC+4)</option>
                <option value="Asia/Karachi">Pakistan (UTC+5)</option>
                <option value="Asia/Dhaka">Bangladesh (UTC+6)</option>
                <option value="Europe/London">London (UTC+0/+1)</option>
                <option value="America/New_York">New York (UTC-5/-4)</option>
                <option value="America/Los_Angeles">Los Angeles (UTC-8/-7)</option>
                <option value="UTC">UTC</option>
              </select>
            </label>
          </div>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="bhrigu-submit">
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Mapping your karmic midpoint..." : "Reveal my Bhrigu Bindu"}
          </button>
        </form>

        {/* Result */}
        {result && (
          <div className="mt-10 space-y-6" data-testid="bhrigu-result">
            {/* Hero */}
            <div className="rounded-2xl bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40">
              <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest mb-2">
                <Target size={14} weight="fill" /> Your Bhrigu Bindu
              </div>
              <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight">
                House {result.bhrigu_bindu.house} · {result.bhrigu_bindu.rashi} · {result.bhrigu_bindu.nakshatra}
              </h2>
              <div className="mt-2 text-amber-200/80 text-sm">
                {result.bhrigu_bindu.degrees_in_rashi}° · Pada {result.bhrigu_bindu.pada} ·
                {" "}ruled by {result.bhrigu_bindu.rashi_lord} (sign) & {result.bhrigu_bindu.nakshatra_lord} (nakshatra)
              </div>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <MidpointPill label="Your Moon" rashi={result.moon.rashi} sub={result.moon.nakshatra} />
                <MidpointPill label="Your Rahu" rashi={result.rahu.rashi} sub={result.rahu.nakshatra} />
                <MidpointPill label="→ Midpoint" rashi={result.bhrigu_bindu.rashi} sub={`${result.bhrigu_bindu.degrees_in_rashi}°`} accent />
              </div>
            </div>

            {/* Soul mission */}
            <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="bhrigu-soul-mission">
              <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-3">
                <Sparkle size={14} weight="duotone" /> Your soul-mission · synthesised reading
              </div>
              <p className="text-sm sm:text-base leading-relaxed text-[#3F2D1B]">
                {result.soul_mission}
              </p>
            </div>

            {/* Layer-by-layer breakdown + Transits — PREMIUM */}
            <PaywallGate
              tool="bhrigu"
              toolLabel="Bhrigu Bindu reading"
              birthDetails={form}
              report={result}
            >
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <LayerCard
                    icon={House}
                    label={`House ${result.bhrigu_bindu.house}`}
                    title={result.house_meaning.name}
                    themes={result.house_meaning.themes}
                    body={result.house_meaning.soul_mission}
                  />
                  <LayerCard
                    icon={Compass}
                    label={`Sign · ${result.sign_meaning.sign}`}
                    title={result.sign_meaning.keyword}
                    body={`Soul purpose flavoured by ${result.sign_meaning.sign}: ${result.sign_meaning.purpose}`}
                    badge={`Ruled by ${result.sign_meaning.lord}`}
                  />
                  <LayerCard
                    icon={MoonStars}
                    label={`Nakshatra · ${result.nakshatra_meaning.nakshatra}`}
                    title={`Pada ${result.nakshatra_meaning.pada} · Ruled by ${result.nakshatra_meaning.ruling_planet}`}
                    body={`The nakshatra lord ${result.nakshatra_meaning.ruling_planet} represents ${result.nakshatra_meaning.planet_role} This planet's themes are layered into your karmic mission — its dasha periods will be especially activating.`}
                  />
                  <LayerCard
                    icon={Star}
                    label={`Sign-lord · ${result.sign_lord_role.planet}`}
                    title={`${result.sign_lord_role.planet} is the engine`}
                    body={`${result.sign_lord_role.planet} represents ${result.sign_lord_role.role} Wherever ${result.sign_lord_role.planet} sits in your chart, in what sign and which house, becomes the *delivery mechanism* of your Bhrigu Bindu destiny.`}
                  />
                </div>

                {/* Transits */}
                <TransitsPanel transits={result.transits} bb={result.bhrigu_bindu} />
              </div>
            </PaywallGate>

            {/* Methodology */}
            <details className="rounded-2xl border border-soft bg-white/60 overflow-hidden">
              <summary className="cursor-pointer list-none px-5 py-3 flex items-center gap-2 text-sm text-[#3F2D1B] hover:bg-amber-50/40">
                <ArrowsInLineHorizontal size={16} weight="duotone" className="text-gold" />
                How we compute Bhrigu Bindu
              </summary>
              <div className="px-5 pb-4 pt-1 text-xs text-[#3F2D1B]/80 space-y-2 leading-relaxed">
                <p>
                  Your sidereal Moon longitude was <b>{result.inputs.moon_longitude}°</b> and Rahu was at <b>{result.inputs.rahu_longitude}°</b>.
                  We take the midpoint of the arc going <b>zodiacally forward from Rahu → Moon</b> (the classical Bhrigu Nadi direction: Rahu = karmic seed, Moon = soul/mind, so the arc Rahu→Moon describes the destiny journey).
                </p>
                <p>
                  Result: Bhrigu Bindu at <b>{result.bhrigu_bindu.longitude}°</b> sidereal — which falls in <b>{result.bhrigu_bindu.rashi}</b>, nakshatra <b>{result.bhrigu_bindu.nakshatra}</b> (pada {result.bhrigu_bindu.pada}). Whole-sign house from your Lagna ({result.lagna.rashi}): <b>{result.bhrigu_bindu.house}</b>.
                </p>
                <p>
                  Transit hits are scanned across the past {result.transits.window_years_back} years and the next {result.transits.window_years_fwd} years for the four karmic-trigger bodies — Jupiter, Saturn, Rahu and Ketu. Each crossing is bisected to ±0.5-day precision.
                </p>
              </div>
            </details>

            {/* CTA — Bhrigu Nadi specialist */}
            <ToolAstrologerCta
              requiredTechnique="Bhrigu Nandi Nadi"
              pitch={`Want it read live? — your ${result.bhrigu_bindu.rashi} Bhrigu Bindu, past-life threads & dasha timing`}
              ctaLabel="Talk to"
            />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ---------- Sub-components ----------

function MidpointPill({ label, rashi, sub, accent }) {
  return (
    <div className={`rounded-lg px-3 py-2 border ${accent ? "bg-amber-300/20 border-amber-300/50" : "bg-amber-900/40 border-amber-700/30"}`}>
      <div className={`text-[10px] uppercase tracking-widest ${accent ? "text-amber-200" : "text-amber-300/80"}`}>{label}</div>
      <div className={`mt-0.5 font-serif-display text-base sm:text-lg leading-tight ${accent ? "text-amber-200" : "text-amber-50"}`}>
        {rashi || "—"}
      </div>
      <div className="text-[10px] text-amber-200/70 mt-0.5">{sub || ""}</div>
    </div>
  );
}

function LayerCard({ icon: Icon, label, title, themes, body, badge }) {
  return (
    <div className="card-surface rounded-2xl p-5">
      <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-2">
        <Icon size={14} weight="duotone" /> {label}
      </div>
      <h3 className="font-serif-display text-lg sm:text-xl text-[#3F2D1B]">{title}</h3>
      {badge && (
        <div className="inline-block mt-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
          {badge}
        </div>
      )}
      {themes && themes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {themes.map((t) => (
            <span key={t} className="text-[11px] px-2 py-0.5 rounded-full border border-soft bg-amber-50/40 text-[#3F2D1B]">
              {t}
            </span>
          ))}
        </div>
      )}
      <p className="mt-3 text-sm text-[#3F2D1B]/85 leading-relaxed">{body}</p>
    </div>
  );
}

const PLANET_ACCENT = {
  Jupiter: { bg: "bg-amber-100", border: "border-amber-300", text: "text-amber-900", tone: "Grace, expansion, dharmic teaching arrives" },
  Saturn:  { bg: "bg-slate-100", border: "border-slate-300", text: "text-slate-800", tone: "Hard work, discipline, karmic accountability lands" },
  Rahu:    { bg: "bg-indigo-100", border: "border-indigo-300", text: "text-indigo-900", tone: "Sudden hunger, foreign chapter, obsession activates" },
  Ketu:    { bg: "bg-rose-100", border: "border-rose-300", text: "text-rose-900", tone: "Detachment, loss, moksha-pull intensifies" },
};

function TransitsPanel({ transits, bb }) {
  const past = transits.past || [];
  const upcoming = transits.upcoming || [];
  return (
    <div className="card-surface rounded-2xl p-5 sm:p-7" data-testid="bhrigu-transits">
      <div className="label-eyebrow text-gold flex items-center gap-1.5 mb-1">
        <CalendarBlank size={14} weight="duotone" /> Karmic milestone transits · ±{transits.window_years_back}/{transits.window_years_fwd}y window
      </div>
      <p className="text-xs text-muted mb-4 max-w-xl">
        Dates when slow karmic-trigger planets (Jupiter, Saturn, Rahu, Ketu) cross your exact Bhrigu Bindu degree at <b>{bb.rashi} {bb.degrees_in_rashi}°</b> — expect life-defining chapters around these days.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-rose-700 mb-2">
            <ArrowDown size={13} weight="bold" /> Past hits
          </div>
          {past.length === 0 ? (
            <p className="text-xs text-muted italic">No major karmic crossings in the past window — your last big chapter may pre-date this scan or come exclusively from your current dasha sequence.</p>
          ) : (
            <ul className="space-y-2">
              {past.map((t, i) => <TransitRow key={`p-${i}`} hit={t} />)}
            </ul>
          )}
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-700 mb-2">
            <ArrowUp size={13} weight="bold" /> Upcoming hits
          </div>
          {upcoming.length === 0 ? (
            <p className="text-xs text-muted italic">No major karmic crossings in the upcoming window. Calm decade ahead — or watch your dashas more closely.</p>
          ) : (
            <ul className="space-y-2">
              {upcoming.map((t, i) => <TransitRow key={`u-${i}`} hit={t} />)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function TransitRow({ hit }) {
  const acc = PLANET_ACCENT[hit.planet] || { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", tone: "" };
  return (
    <li className={`rounded-xl border ${acc.border} ${acc.bg} p-3 flex items-center gap-3`} data-testid={`bhrigu-transit-${hit.planet}-${hit.date}`}>
      <div className={`font-serif-display text-base ${acc.text} min-w-[68px]`}>{hit.planet}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#3F2D1B]">{formatDate(hit.date)}</div>
        <div className="text-[11px] text-[#3F2D1B]/70">{acc.tone}</div>
      </div>
    </li>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}
